import { Button, Flex, Form, Radio, Space } from "antd";

import "./style.css";
import SearchForm, { SearchFormValues } from "@/components/SearchForm";
import { useRef, useState } from "react";
import { useForm } from "antd/es/form/Form";
import CustomSearchBox from "@/components/SearchForm/components/CustomSearchBox";
import RadioGroupCard from "@/components/SearchForm/components/RadioGroupCard/RadioGroupCard";
import { EcrfConditions } from "@/data";
import DiseaseGrid from "@/components/SearchForm/components/DiseaseGrid/DiseaseGrid";
import LocationAutocomplete from "@/components/SearchForm/components/LocationAutocomplete";
import { AgeOptions, DistanceOptions, GenderOptions } from "@/components/SearchForm/constants";
import StudyTypes from "@/components/SearchForm/components/StudyTypes";
import { filterUpdateEvent } from "@/utils/analytics/googleAnalytics";
import LinkButton from "@/components/LinkButton";
import { scrollElementIntoView } from "@/utils/iframeUtils";

import { useMediaQuery } from "react-responsive";
import SponsorSearchForStudy from "@/components/SponsorSearchForStudy";

function ECRFForm() {
    const [form] = useForm<SearchFormValues>();
    const [diseaseOptions, setDiseaseOptions] = useState<string>("ecrf");

    const isXSmallScreen = useMediaQuery({
        query: "(max-width: 400px)",
    });

    return (
        <SearchForm form={form} size={isXSmallScreen ? "small" : "middle"}>
            {(stepsProps) => (
                <>
                    <div ref={stepsProps.stepRefs.topOfMenu} />
                    <div className="ecrf-form-grid">
                        <Form.Item name="condition" valuePropName="value" trigger="onValueChange">
                            <CustomSearchBox handleSubmit={form.submit} />
                        </Form.Item>
                        <Form.Item name="subset" initialValue="ecrf">
                            <RadioGroupCard
                                options={EcrfConditions}
                                value={diseaseOptions}
                                onChange={(value) => {
                                    setDiseaseOptions(value);
                                }}
                            />
                        </Form.Item>
                        {diseaseOptions === "all" && (
                            <Form.Item shouldUpdate={(prev, next) => prev.condition !== next.condition}>
                                {({ getFieldValue, setFieldsValue }) => (
                                    <DiseaseGrid
                                        value={getFieldValue("condition")}
                                        onChange={(value) => {
                                            filterUpdateEvent("condition", value, "icons");
                                            setFieldsValue({ condition: value });
                                            stepsProps.scrollToStep(stepsProps.stepRefs.stepTwo);
                                        }}
                                    />
                                )}
                            </Form.Item>
                        )}
                        <div className="ecrf-form-row" ref={stepsProps.stepRefs.stepTwo}>
                            <Form.Item
                                name="location"
                                valuePropName="value"
                                trigger="onChange"
                                label="What is your location? (optional)"
                                className="form-question"
                            >
                                <LocationAutocomplete />
                            </Form.Item>
                            <Form.Item
                                name="distanceInMeters"
                                label="How far can you travel? (miles)"
                                className="form-question"
                            >
                                <Radio.Group
                                    options={DistanceOptions}
                                    optionType="button"
                                    buttonStyle="solid"
                                    onChange={() => stepsProps.scrollToStep(stepsProps.stepRefs.stepThree)}
                                    block
                                />
                            </Form.Item>
                        </div>
                        <div className="ecrf-form-row" ref={stepsProps.stepRefs.stepThree}>
                            <Form.Item name="age" label="What is your age?" className="form-question">
                                <Radio.Group options={AgeOptions} optionType="button" buttonStyle="solid" block />
                            </Form.Item>
                            <Form.Item name="gender" label="What is your sex?" className="form-question">
                                <Radio.Group
                                    options={GenderOptions.map((v) => ({
                                        value: v.value,
                                        label: (
                                            <Space>
                                                {v.icon}
                                                {v.label}
                                            </Space>
                                        ),
                                    }))}
                                    optionType="button"
                                    buttonStyle="solid"
                                    onChange={() => stepsProps.scrollToStep(stepsProps.stepRefs.stepFour)}
                                    block
                                />
                            </Form.Item>
                        </div>
                        <div className="ecrf-study-grid" ref={stepsProps.stepRefs.stepFour}>
                            <div className="select-parameter-label main-header main-header-init">
                                Are you looking for a Medication, Device, or Survey study?
                            </div>
                            <div className="sub-header-light text-center">Select all that apply</div>
                            <Form.Item name="studyTypes">
                                <StudyTypes />
                            </Form.Item>
                        </div>
                    </div>
                    <Flex
                        justify="center"
                        className={`sticky-buttons ${stepsProps.showSticky ? "active" : ""}`}
                        ref={stepsProps.stepRefs.stepFive}
                    >
                        <Space size="large" className="search-form-actions">
                            <Button onClick={stepsProps.scrollToTop} type="default">
                                Back to Top
                            </Button>
                            <Button type="primary" htmlType="submit">
                                See My Results
                            </Button>
                        </Space>
                    </Flex>
                </>
            )}
        </SearchForm>
    );
}

function ECRFHomepage() {
    const paramsRef = useRef<HTMLDivElement>(null);
    const handleGetStartedClick = () => {
        scrollElementIntoView(paramsRef.current, { behavior: "smooth" });
    };

    return (
        <div className="ecrf-page">
            <div className="ecrf-hero">
                <div className="home-header">
                    <div className="home-container">
                        <h1>Empower Yourself</h1>
                        <div className="hero-text">
                            <Flex vertical justify="space-between">
                                <div>
                                    <h3>Join a Trial</h3>
                                    <p>
                                        <span className="line">
                                            Find Clinical Trials researching advances in Endometrial Cancers
                                        </span>{" "}
                                        and related conditions, <strong>all in plain language</strong>
                                    </p>
                                </div>

                                <div>
                                    <Button type="primary" shape="round" size="large" onClick={handleGetStartedClick}>
                                        Get Started
                                    </Button>
                                </div>
                            </Flex>
                            <div>
                                <div className="divider" />
                            </div>
                            <Flex vertical justify="space-between">
                                <div>
                                    <h3>Learn from Research</h3>
                                    <p>
                                        Visit ScienceMyHealth to learn more about clinical trials and learn about the
                                        latest in medical research - all in plain language
                                    </p>
                                </div>
                                <div>
                                    <LinkButton
                                        to="https://sciencemyhealth.com"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        type="primary"
                                        shape="round"
                                        size="large"
                                    >
                                        Visit ScienceMyHealth
                                    </LinkButton>
                                </div>
                            </Flex>
                        </div>
                    </div>
                </div>
                <div className="home-header blob"></div>
            </div>
            <div className="content-body ecrf-content" ref={paramsRef}>
                <ECRFForm />
            </div>
            <SponsorSearchForStudy />
        </div>
    );
}

export default ECRFHomepage;
